import { Navigate, Route, Routes, useLocation } from 'react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../features/auth/useAuth.ts';
import { AppLayout } from '../components/AppLayout.tsx';
import { AuthLayout } from '../components/AuthLayout.tsx';
import { FullScreenMessage } from '../components/FullScreenMessage.tsx';
import { LoginPage } from '../pages/LoginPage.tsx';
import { RegisterPage } from '../pages/RegisterPage.tsx';
import { LeaguesPage } from '../pages/LeaguesPage.tsx';
import { CreateLeaguePage } from '../pages/CreateLeaguePage.tsx';
import { JoinLeaguePage } from '../pages/JoinLeaguePage.tsx';
import { LeagueDetailPage } from '../pages/LeagueDetailPage.tsx';
import { PredictionsPage } from '../pages/PredictionsPage.tsx';
import { StandingsPage } from '../pages/StandingsPage.tsx';
import { NotFoundPage } from '../pages/NotFoundPage.tsx';

/**
 * Ruta protegida. Mientras se comprueba la sesión no se decide nada: pintar el login
 * durante ese instante haría parpadear la pantalla en cada recarga de alguien que sí
 * está conectado.
 *
 * Esto es comodidad de interfaz, NO seguridad: quien manipule el estado del cliente
 * verá el cascarón de la página y ni un dato, porque cada endpoint vuelve a exigir
 * sesión por su cuenta.
 */
function RequireAuth({ children }: { children: ReactElement }) {
  const { status } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (status === 'checking') return <FullScreenMessage text={t('common.loading')} />;
  if (status === 'anonymous') {
    // Se recuerda a dónde iba para devolverlo ahí tras identificarse.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <AppLayout>{children}</AppLayout>;
}

/** Ruta pública de identificación: a quien ya tiene sesión no se le enseña. */
function PublicOnly({ children }: { children: ReactElement }) {
  const { status } = useAuth();
  const { t } = useTranslation();

  if (status === 'checking') return <FullScreenMessage text={t('common.loading')} />;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <AuthLayout>{children}</AuthLayout>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnly>
            <RegisterPage />
          </PublicOnly>
        }
      />

      <Route
        path="/"
        element={
          <RequireAuth>
            <LeaguesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/leagues/new"
        element={
          <RequireAuth>
            <CreateLeaguePage />
          </RequireAuth>
        }
      />
      <Route
        path="/leagues/join"
        element={
          <RequireAuth>
            <JoinLeaguePage />
          </RequireAuth>
        }
      />
      <Route
        path="/leagues/:leagueId"
        element={
          <RequireAuth>
            <LeagueDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/leagues/:leagueId/predictions"
        element={
          <RequireAuth>
            <PredictionsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/leagues/:leagueId/standings"
        element={
          <RequireAuth>
            <StandingsPage />
          </RequireAuth>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
